import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalDialogType, OrderTransactionStatus, VoidOrderOperationType } from 'src/app/@core/constants';
import { PosConfirmationService } from 'src/app/@core/services/pos-confirmation.service';
import { PosPrintDocumentsModalService } from 'src/app/@core/services/pos-print-documents-modal.service';
import { ColumnTable } from 'src/app/models/column-table';
import { Customer } from 'src/app/models/customer';
import { DigitalSignature } from 'src/app/models/digital-signature';
import { Invoice } from 'src/app/models/invoice';
import { Loan } from 'src/app/models/loan';
import { Order } from 'src/app/models/order';
import { OrderCompleteProcess } from 'src/app/models/order-complete-process';
import { OrderTransaction } from 'src/app/models/order-transation';
import { Paging } from 'src/app/models/page';
import { Payment } from 'src/app/models/payment';
import { ProductCart } from 'src/app/models/product-cart';
import { PaymentsRequestModel } from 'src/app/models/request/PaymentRequestModel';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ExceptionSolutionService } from 'src/app/services/exception-solution.service';
import { InvoiceService } from 'src/app/services/invoice.service';
import { LoanService } from 'src/app/services/loan.service';
import { OrderService } from 'src/app/services/order.service';
import { TransactionService } from 'src/app/services/transaction.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-return-invoices-details',
  templateUrl: './return-invoices-details.component.html',
  styleUrls: ['./return-invoices-details.component.sass']
})
export class ReturnInvoicesDetailsComponent implements OnInit {

  order: Order = new Order();
  orderComplete: OrderCompleteProcess = new OrderCompleteProcess();
  customer: Customer = new Customer();
  invoices: Paging<Array<Invoice>> = new Paging([]);
  loans: Paging<Array<Loan>> = new Paging([]);
  customerInformation: string = "";
  sellerInformation: string = "";
  productsCart: Paging<Array<ProductCart>> = new Paging([]);
  transactions: Paging<Array<OrderTransaction>> = new Paging([]);
  constructor(private route: ActivatedRoute, private router: Router, private orderSerivce: OrderService, private confirmService: PosConfirmationService, private configService: ConfigurationService, private transactionService: TransactionService, private readonly exceptionSolutionService: ExceptionSolutionService, private loanService: LoanService, private printModalService: PosPrintDocumentsModalService, private customerService: CustomerService, private invoiceService: InvoiceService) {
  }

  async ngOnInit(): Promise<void> {
    this.orderComplete = (await this.orderSerivce.readerOrderCompleteProcess(this.route.snapshot.params.code).toPromise()).response;
    this.customer = (await this.customerService.readCustomerByCode(this.orderComplete.order.customerCode).toPromise());
    this.order = this.orderComplete.order
    this.loadProductsCart();
    this.customerInformation = `[${this.order.customerCode}] - ${this.order.customerName} ${this.order.customerLastName}`
    this.sellerInformation = `[${this.order.vendor}] - ${this.order.vendorName}`
    this.invoices = new Paging(this.orderComplete.invoices)
    this.loans = (await this.loanService.getCustomerLoans({ page: 0, pageSize: 1000, value: { invoiceNumber: this.order.invoiceNumber, customerCode: this.order.customerCode } }).toPromise()).response;

    // this.loans = new Paging(this.loans.content.filter(f => f.invoices.some(s => s.invoiceCode == this.order.invoiceNumber)));
    this.transactions = new Paging(this.orderComplete.transactions);

    if (this.route.snapshot.queryParams.print) {
      await this.print();
    }
  }

  getInvoiceHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable("fullNumber", "Número de Nota de Crédito"),
      new ColumnTable("totals.netTotal", "Total", { type: "currency" }),
      new ColumnTable("voidOrderOperationType", "Operación", { type: "badge", valueMap: this.getVoidOperationMap() }),
      new ColumnTable("usableAsPayment", "Habilitada para utilizar", { type: "switch" }),
      new ColumnTable("isActivated", "Activado", { type: "switch" }),
      new ColumnTable("code", "Activar", {
        type: "action",
        actions: [{
          fn: async (invoice: Invoice) => {
            const result = await this.confirmService.showAsync("¡Advertencia!", "¿Esta seguro de querer activar esta nota crédito?", ModalDialogType.SUCCESS)
            if (result) {

              if (!await this.validateInvoicePayments(invoice.originalOrderCode)) {
                this.exceptionSolutionService.showAlertWithSolution(`Devolución de Factura ${invoice.fullNumber}`, `Por favor recaude la factura de taller antes de activar la nota de credito.`);
                return;
              }
              const model = new PaymentsRequestModel();
              model.isForActiveCreditNote = true;
              model.reference = invoice.fullNumber?.replace("-", "") || "";
              model.branchCode = invoice.branchCode;
              model.salesOrgCode = invoice.salesOrg;
              const payment = new Payment();
              payment.amount = -invoice.amountUsedAsPayment;
              payment.paymentMethodCode = "ZN02";
              payment.currency = "HNL";
              model.payments.push(payment);
              await this.invoiceService.activateCreditNote(model, invoice.customerCode, invoice.fullNumber || "").toPromise();

              Swal.fire({
                title: `Devolución`,
                html: `Se activo la nota de credito ${invoice.fullNumber} exitosamente. Ya se puede utilizar para pagos.`,
                icon: 'success',
                confirmButtonText: 'Aceptar',
              });
              await this.ngOnInit();
            }
          },
          icon: "fa fa-power-off text-primary",
          tooltip: "Activar",
          isDisabled: false,
          disabledFn: (invoice: Invoice) => invoice.repairshopOrderSubType != "ZFST" || invoice.isActivated,
          isPrivileged: true,
          privilege: "doActivateRepairshop"
        }]
      })
    ]
  }

  getLoanHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable("contractNumber", "Número de Contrato"),
      new ColumnTable("amount", "Total", { type: "currency" }),
      new ColumnTable("creationDate", "Fecha de creación", { type: "date", dateType: "dateFromString" }),
      new ColumnTable("lastPaymentDate", "Fecha última cuota pagada", { type: "date" })
      //"lastPaymentDate" : ISODate("2023-02-28T15:31:44.174+0000")
    ]
  }

  getReceiptHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable("code", "Número de Recibo"),
      new ColumnTable("netTotal", "Total", { type: "currency" })
    ]
  }

  async return() {
    await this.returnProcess(VoidOrderOperationType.RETURN);
  }
  async cancel() {
    await this.returnProcess(VoidOrderOperationType.CANCEL);
  }
  async voidOperation() {
    await this.returnProcess(VoidOrderOperationType.VOID);
  }
  validateReturned() {
    return this.invoices.content.length > 0;
  }

  validateCanceled() {
    return !this.order.orderEntryList.some(s => s.isForDelivery) || this.invoices.content.length > 0;
  }

  validateVoided() {
    return this.validateCanceled();
  }

  async returnProcess(operation: VoidOrderOperationType) {
    let result = await this.confirmService.showAsync("Devolución de Factura", `¿Esta seguro de ${this.getVoidOperationDescription(operation)} esta factura?`, ModalDialogType.SUCCESS);
    if (result) {
      let resultDiffBranch = true;
      if (this.order.branchCode != this.configService.readSelectedBranchAndSalesOrg().branch.code) {
        resultDiffBranch = false;
        resultDiffBranch = await this.confirmService.showAsync("Devolución de Factura", `El centro donde se creó la factura es diferente al centro de devolución, ¿desea continuar?`, ModalDialogType.WARNING);
      }
      if (resultDiffBranch) {
        if (this.order.condition == "contado") {
          this.router.navigate(["returnInvoices", "createCreditNote", this.order.code, operation]);
        }
        else {
          this.router.navigate(["returnInvoices", "createContractCreditNote", this.order.code, operation]);
        }
      }
    }
  }
  getVoidOperationDescription(operation: VoidOrderOperationType) {
    switch (operation) {
      case VoidOrderOperationType.CANCEL:
        return "cancelar";
      case VoidOrderOperationType.RETURN:
        return "devolver";
      case VoidOrderOperationType.VOID:
        return "anular";
      default:
        return "devolver";
    }
  }

  loadProductsCart() {
    this.productsCart = new Paging(this.order.orderEntryList.map(o => {
      let result = new ProductCart();
      result.productCode = o.productCode;
      result.description = o.productName;
      result.amount = o.totalQuantity;
      result.serial = o.serialNumber;
      result.price = o.grossTotal;
      result.discountTotal = o.totals.discountTotal;
      result.taxTotal = o.totals.taxTotal;
      result.total = o.netTotal;

      return result;
    }))
  }

  getProductCartHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable("amount", "#", { type: "number", iconClass: "fa fa-shopping-cart", isIcon: true }),
      new ColumnTable("productCode", "Código", { isSmall: true }),
      new ColumnTable("description", "Descripción", { isSmall: true }),
      new ColumnTable("serial", "Serie", { type: "badge" }),
      new ColumnTable("price", "Sub-Total", { type: "currency" }),
      new ColumnTable("discountTotal", "Descuento", { type: "currency" }),
      new ColumnTable("taxTotal", "Impuesto", { type: "currency" }),
      new ColumnTable("total", "Total", { type: "currency" })
    ]
  }

  getTransactionsHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable("code", "Código"),
      new ColumnTable("status", "Estado", { type: "badge", valueMap: this.getOrderTransactionStatusMap() }),
      new ColumnTable("code", "Reprocesar", {

        type: "action",
        actions: [
          {
            fn: async (transaction: OrderTransaction) => {
              await this.confirmService.show(`Reenvio de transacción ${transaction.code}`, "¿Está seguro de reenviar esta transacción?", ModalDialogType.SUCCESS, async () => {
                await this.transactionService.processTransaction(transaction.code).toPromise();
                window.location.reload();
              });
            },
            icon: "fa fa-refresh text-warning",
            tooltip: "Reprocesar",
            isDisabled: false,
            disabledFn: (transaction: OrderTransaction) => transaction.status == OrderTransactionStatus.FINISHED
          }
        ]
      })
    ]
  }
  getOrderTransactionStatusMap(): Map<string, string> {
    let result = new Map<string, string>();
    result.set("NEW", "Nuevo");
    result.set("INVOICES", "Creando Nota de Crédito");
    result.set("LOANS", "Devolviendo Contratos");
    result.set("PAYMENTS", "Devolviendo Pagos");
    result.set("FINISHED", "Terminado");
    result.set("NO_STATUS", "Sin Estado");

    return result;
  }


  getVoidOperationMap(): Map<string, string> {
    let result = new Map<string, string>();
    result.set("VOID", "Anulación");
    result.set("CANCEL", "Cancelación");
    result.set("RETURN", "Devolución");
    return result;
  }

  async print() {
    await this.printModalService.show(this.orderComplete, this.customer, new DigitalSignature());
  }


  private async validateInvoicePayments(originalOrderCode: string) {
    const completeOrder = (await this.orderSerivce.readerOrderCompleteProcess(originalOrderCode).toPromise()).response;
    const payments: Payment[] = completeOrder.payments;

    if (payments.length > 0) {

      const sum = payments.reduce((acc, currentPayment) => {
        acc += currentPayment.amount;
        return acc;
      }, 0);

      return sum > 0;

    }
    return false;
  }
}
